"use client";

import { useEffect, useState } from "react";

/**
 * Returns true once it is safe to load non-critical third-party scripts:
 * after LCP, or when the browser reports idle time, or on first user
 * interaction — whichever comes first.
 */
export function useDeferredScripts(): boolean {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;

        let fired = false;
        const fire = () => {
            if (fired) return;
            fired = true;
            setReady(true);
        };

        let lcpObserver: PerformanceObserver | undefined;
        try {
            lcpObserver = new PerformanceObserver((list) => {
                if (list.getEntries().length) fire();
            });
            lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
        } catch {
            // PerformanceObserver / LCP not supported — idle/interaction fallbacks handle it.
        }

        const supportsIdle = "requestIdleCallback" in window;
        const idleHandle = supportsIdle
            ? (window as Window).requestIdleCallback!(fire, { timeout: 4000 })
            : window.setTimeout(fire, 2500);

        const interactionEvents: (keyof WindowEventMap)[] = ["scroll", "pointerdown", "keydown"];
        interactionEvents.forEach((event) => {
            window.addEventListener(event, fire, { once: true, passive: true });
        });

        return () => {
            lcpObserver?.disconnect();
            if (supportsIdle) {
                (window as Window).cancelIdleCallback!(idleHandle as number);
            } else {
                clearTimeout(idleHandle as unknown as ReturnType<typeof setTimeout>);
            }
            interactionEvents.forEach((event) => {
                window.removeEventListener(event, fire);
            });
        };
    }, []);

    return ready;
}
